/**
 * Trial Context
 * Manages trial state across the application
 * Works alongside existing OrganizationContext without interfering
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from './OrganizationContext';
import { useError } from './ErrorContext';
import TrialService from '../services/TrialService';
import { canAccessApp } from '../utils/trialUtils';

const TrialContext = createContext();

export function TrialProvider({ children }) {
  const { organizationId, isApproved, organizationData, loading: orgLoading } = useOrganization();
  const { showErrorToast } = useError();
  const [trialState, setTrialState] = useState({
    loading: false,
    error: null,
    trialStatus: null,
    daysRemaining: 0,
    trialProgress: 0,
    isTrialExpired: false,
    canAccessApp: false,
    lastUpdate: null
  });

  // Ref to track previous subscription status to avoid infinite loops
  const prevSubscriptionStatusRef = useRef(null);

  // Update trial state
  const updateTrialState = useCallback((updates) => {
    setTrialState(prev => ({
      ...prev,
      ...updates,
      lastUpdate: new Date().toISOString()
    }));
  }, []);

  // Fetch trial status
  const fetchTrialStatus = useCallback(async () => {
    if (!organizationId || orgLoading) {
      return;
    }

    try {
      updateTrialState({ loading: true, error: null });

      const trialStatus = await TrialService.getTrialStatus(organizationId);
      
      // Calculate if user can access the app
      // FIX: Issue #3 - Pass subscriptionEndsAt to canAccessApp for expiration check
      const canAccess = canAccessApp(
        isApproved,
        trialStatus.subscriptionStatus,
        trialStatus.trialStatus,
        organizationData?.bypassLimits,
        trialStatus.subscriptionEndsAt || null
      );



      updateTrialState({
        loading: false,
        error: null,
        trialStatus,
        daysRemaining: trialStatus.daysRemaining,
        trialProgress: trialStatus.trialProgress,
        isTrialExpired: trialStatus.isTrialExpired,
        canAccessApp: canAccess
      });

    } catch (error) {
      updateTrialState({
        loading: false,
        error: error.message || 'Failed to fetch trial status'
      });
    }
  }, [organizationId, isApproved, orgLoading, organizationData, updateTrialState]);

  // Check for trial notifications
  const checkTrialNotifications = useCallback(async () => {
    if (!organizationId) return;

    try {
      const notifications = await TrialService.checkTrialNotifications(organizationId);
      
      // Here you could dispatch notifications to a notification service
      // For now, we'll just log them
      if (notifications.length > 0) {
        notifications.forEach(notification => {
          showErrorToast(notification.message, {
            variant: notification.priority === 'urgent' ? 'error' : 'warning',
            autoHide: true,
            duration: notification.priority === 'urgent' ? 6000 : 4000
          });
        });

        await Promise.all(
          notifications.map(notification =>
            TrialService.markNotificationSent(organizationId, notification.type).catch(() => {})
          )
        );
      }
    } catch (error) {
      // Error checking trial notifications - handled silently
    }
  }, [organizationId, showErrorToast]);

  // Convert trial to subscription
  const convertTrialToSubscription = useCallback(async (subscriptionData) => {
    if (!organizationId) {
      showErrorToast('Organization ID is required', {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    try {
      updateTrialState({ loading: true, error: null });
      
      await TrialService.convertTrialToSubscription(organizationId, subscriptionData);
      
      // Refresh trial status after conversion
      await fetchTrialStatus();
      
    } catch (error) {
      updateTrialState({
        loading: false,
        error: error.message || 'Failed to convert trial to subscription'
      });
      throw error;
    }
  }, [organizationId, fetchTrialStatus, updateTrialState, showErrorToast]);

  // Extend trial (admin function)
  const extendTrial = useCallback(async (additionalDays = 7) => {
    if (!organizationId) {
      showErrorToast('Organization ID is required', {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    try {
      updateTrialState({ loading: true, error: null });
      
      await TrialService.extendTrial(organizationId, additionalDays);
      
      // Refresh trial status after extension
      await fetchTrialStatus();
      
    } catch (error) {
      updateTrialState({
        loading: false,
        error: error.message || 'Failed to extend trial'
      });
      throw error;
    }
  }, [organizationId, fetchTrialStatus, updateTrialState, showErrorToast]);

  // Mark notification as sent
  const markNotificationSent = useCallback(async (notificationType) => {
    if (!organizationId) return;

    try {
      await TrialService.markNotificationSent(organizationId, notificationType);
    } catch (error) {
      // Error marking notification as sent - handled silently
    }
  }, [organizationId]);

  // Get trial analytics
  const getTrialAnalytics = useCallback(async () => {
    if (!organizationId) return null;

    try {
      return await TrialService.getTrialAnalytics(organizationId);
    } catch (error) {
      return null;
    }
  }, [organizationId]);

  // Effect to fetch trial status when organization changes
  useEffect(() => {
    if (organizationId && !orgLoading) {
      fetchTrialStatus();
    } else {
      // Clear trial state when no organization
      setTrialState({
        loading: false,
        error: null,
        trialStatus: null,
        daysRemaining: 0,
        trialProgress: 0,
        isTrialExpired: false,
        canAccessApp: false,
        lastUpdate: null
      });
    }
  }, [organizationId, orgLoading, fetchTrialStatus]);

  // Effect to refresh trial status when organization data changes
  // This ensures TrialContext stays in sync with OrganizationContext
  useEffect(() => {
    if (!organizationId || orgLoading || !organizationData) {
      return;
    }

    // Check if subscription status actually changed
    // Create a stable key based on subscription-related fields
    const currentStatus = organizationData.subscriptionStatus;
    const currentPlan = organizationData.subscriptionPlan;
    const currentBypass = organizationData.bypassLimits;
    
    const statusKey = `${currentStatus}-${currentPlan}-${currentBypass}`;
    
    // Only refresh if subscription-related fields actually changed
    if (prevSubscriptionStatusRef.current !== statusKey) {
      prevSubscriptionStatusRef.current = statusKey;
      
      // Refresh trial status when subscription-related fields change
      // This ensures TrialContext updates when OrganizationContext detects subscription changes
      fetchTrialStatus();
    }
  }, [organizationId, organizationData, orgLoading, fetchTrialStatus]);

  // Effect to check notifications periodically
  useEffect(() => {
    if (!organizationId) return;

    // Check notifications immediately
    checkTrialNotifications();

    // Check notifications every hour
    const interval = setInterval(checkTrialNotifications, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [organizationId, checkTrialNotifications]);

  // Context value
  const contextValue = {
    // State
    ...trialState,
    
    // Computed values
    isOnTrial: trialState.trialStatus?.isOnTrial || false,
    hasActiveSubscription: trialState.trialStatus?.hasActiveSubscription || false,
    subscriptionStatus: trialState.trialStatus?.subscriptionStatus || null,
    trialStartDate: trialState.trialStatus?.trialStartDate || null,
    trialEndDate: trialState.trialStatus?.trialEndDate || null,
    subscriptionPlan: trialState.trialStatus?.subscriptionPlan || null,
    subscriptionStartDate: trialState.trialStatus?.subscriptionStartDate || null,
    paymentGatewayId: trialState.trialStatus?.paymentGatewayId || null,
    lastPaymentDate: trialState.trialStatus?.lastPaymentDate || null,
    nextBillingDate: trialState.trialStatus?.nextBillingDate || null,
    willNotRenew: trialState.trialStatus?.willNotRenew || false,
    autoRenew: trialState.trialStatus?.autoRenew !== false,
    subscriptionEndsAt: trialState.trialStatus?.subscriptionEndsAt || null,
    
    // Actions
    fetchTrialStatus,
    convertTrialToSubscription,
    extendTrial,
    markNotificationSent,
    getTrialAnalytics,
    
    // Utility functions
    canAccessApp: trialState.canAccessApp,
    isTrialExpired: trialState.isTrialExpired
  };

  return (
    <TrialContext.Provider value={contextValue}>
      {children}
    </TrialContext.Provider>
  );
}

export function useTrial() {
  const context = useContext(TrialContext);
  if (context === undefined) {
    throw new Error('useTrial must be used within a TrialProvider');
  }
  return context;
}

// Hook for checking if user can access the app (combines org approval + trial status)
export function useTrialAccess() {
  const { isApproved } = useOrganization();
  const { canAccessApp, loading, error } = useTrial();

  return {
    canAccess: canAccessApp,
    isApproved,
    loading,
    error
  };
}
